import { Fragment, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { useT } from "../../../../i18n";
import { toNumberOrNull } from "../../../../utils/energyDevices";
import { formatWatt } from "./EnergyKpi";
import { AXIS_COLOR, GRID_STROKE, Y_AXIS_WIDTH, unitLabel } from "./chartStyle";
import "./EnergyInsight.css";

// 실측 6h + 예측 6h 타임라인을 크게 보는 모달. 사이드바 차트의 + 버튼으로 연다.
// 행(data)은 작은 차트가 만든 걸 그대로 받는다 — 시간 축 · 예측 자리 규칙이 두 곳에서 갈리지 않게.
// 타일(현재 시간대 평균 · +1h 예측 · 완료 시간대 오차)을 누르면 그 선을 켜고 끈다. 마지막 하나는 끌 수 없다.
// "실측·예측 비교"를 끄면 지난 시각의 예측 점선을 숨기고 앞으로 6시간 예측만 남긴다. 기본은 켜짐.
// 예측 오차 · 툴팁의 차이는 원본 예측(rawFc)으로 계산한다 — 소비 예측이 비어 0 으로 그린 자리는 비교하지 않는다.

const HOUR_MS = 60 * 60 * 1000;
const NARROW_QUERY = "(max-width: 640px)";
const MODAL_MARGIN = { top: 28, right: 16, left: -12, bottom: 0 };
const MODAL_TICK = { fill: AXIS_COLOR, fontSize: 12 };
const MODAL_BG = "#101721";

const pad = (value) => String(value).padStart(2, "0");
const watt = (value) => (toNumberOrNull(value) == null ? "–" : `${formatWatt(value)} W`);
const signed = (diff) => (diff > 0 ? `+${diff}` : diff < 0 ? `−${-diff}` : "0");

// 좁은 화면이면 눈금을 2시간마다로 줄인다(13개 눈금이 겹친다).
const useNarrow = () => {
  const [narrow, setNarrow] = useState(
    () => typeof window.matchMedia === "function" && window.matchMedia(NARROW_QUERY).matches
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia(NARROW_QUERY);
    const update = () => setNarrow(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  return narrow;
};

const ModalTooltip = ({ active, payload, label, series, rawFc }) => {
  const t = useT();
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;
  const tag = row.offset === 0 ? t("energy.timeline.modal.currentHourAverage") : row.offset > 0 ? t("energy.timeline.modal.forecast") : null;
  return (
    <div className="ei-tl-tip">
      <p className="ei-tl-tip-head">
        {label}
        {tag && <span> · {tag}</span>}
      </p>
      <div className="ei-tl-tip-grid">
        <span />
        <span className="th">{t("energy.timeline.modal.measured")}</span>
        <span className="th">{t("energy.timeline.modal.forecast")}</span>
        {series.map((item) => {
          const measured = toNumberOrNull(row[item.histKey]);
          const predicted = toNumberOrNull(row[item.fcKey]);
          const raw = toNumberOrNull(rawFc.get(row.offset)?.[item.field]);
          const diff = row.offset < 0 && measured != null && predicted != null && raw != null
            ? Math.round(raw) - Math.round(measured) : null;
          return (
            <Fragment key={item.field}>
              <span className="name">
                <i style={{ backgroundColor: item.color }} />
                {t(item.labelKey)}
              </span>
              <span className="val">{watt(measured)}</span>
              <span className="val">
                {watt(predicted)}
                {diff != null && <small>{signed(diff)}</small>}
              </span>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default function EnergyTimelineModal({
  onClose,
  deviceLabel = null,
  nowHour,
  nowLabel,
  nowTick,
  endTick,
  data,
  series,
  rawFc,
  placeholderSegment = null,
}) {
  const t = useT();
  const titleId = useId();
  const narrow = useNarrow();
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const [hidden, setHidden] = useState(() => new Set());
  const [showPast, setShowPast] = useState(true);

  // 열 때 닫기 버튼에 포커스, Esc 로 닫기, 뒤 화면 스크롤 잠금.
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event) => {
      if (event.key === "Escape") onCloseRef.current();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const visible = series.filter((item) => !hidden.has(item.field));
  const toggle = (field) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else if (series.length - next.size > 1) next.add(field);
      return next;
    });

  const chartData = useMemo(() => {
    if (showPast) return data;
    const cleared = Object.fromEntries(series.map((item) => [item.fcKey, null]));
    return data.map((row) => (row.offset > 0 ? row : { ...row, ...cleared }));
  }, [data, series, showPast]);

  const ticks = data.filter((row) => !narrow || row.offset % 2 === 0).map((row) => row.label);
  const nowRow = data.find((row) => row.offset === 0);
  const nextRow = data.find((row) => row.offset === 1);

  const errorFor = (item) => {
    const diffs = data
      .filter((row) => row.offset < 0)
      .map((row) => {
        const measured = toNumberOrNull(row[item.histKey]);
        const predicted = toNumberOrNull(rawFc.get(row.offset)?.[item.field]);
        return measured != null && predicted != null ? Math.abs(predicted - measured) : null;
      })
      .filter((diff) => diff != null);
    return diffs.length ? diffs.reduce((sum, diff) => sum + diff, 0) / diffs.length : null;
  };

  const kst = new Date(nowHour + 9 * HOUR_MS);
  const basis = t("energy.timeline.modal.basis", {
    date: `${pad(kst.getUTCMonth() + 1)}/${pad(kst.getUTCDate())}`,
    h: pad(kst.getUTCHours()),
  });
  const subtitle = [deviceLabel, basis].filter(Boolean).join(" · ");

  return createPortal(
    <div
      className="ei-tl-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="ei-tl-modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="ei-tl-head">
          <h2 id={titleId}>{t("energy.timeline.modal.title")}</h2>
          <p>{subtitle}</p>
        </div>
        <button
          ref={closeRef}
          type="button"
          className="ei-tl-close"
          aria-label={t("energy.timeline.modal.close")}
          onClick={onClose}
        >
          ×
        </button>

        <div className="ei-tl-body">
          <div className="ei-tl-kpis" style={{ gridTemplateColumns: `repeat(${series.length}, minmax(0, 1fr))` }}>
            {series.map((item) => {
              const error = errorFor(item);
              return (
                <button
                  key={item.field}
                  type="button"
                  className="ei-tl-kpi"
                  aria-pressed={!hidden.has(item.field)}
                  onClick={() => toggle(item.field)}
                >
                  <span className="ei-tl-kpi-label">
                    <i style={{ "--c": item.color }} />
                    {t(item.labelKey)}
                    <em>{t("energy.timeline.modal.currentHourAverage")}</em>
                  </span>
                  <span className="ei-tl-kpi-value">
                    {formatWatt(nowRow?.[item.histKey])}
                    <small>W</small>
                  </span>
                  <span className="ei-tl-kpi-sub">
                    <span>
                      {t("energy.timeline.modal.next")} <b>{watt(nextRow?.[item.fcKey])}</b>
                    </span>
                    <span>
                      {t("energy.timeline.modal.error")} <b>{error == null ? "–" : `±${formatWatt(error)} W`}</b>
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="ei-tl-tools">
            <button
              type="button"
              className={`ei-device-pill${showPast ? " active" : ""}`}
              aria-pressed={showPast}
              onClick={() => setShowPast((prev) => !prev)}
            >
              {t("energy.timeline.modal.compare")}
            </button>
          </div>

          <div className="ei-tl-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={MODAL_MARGIN}>
                <CartesianGrid stroke={GRID_STROKE} vertical={false} />
                <ReferenceArea
                  x1={nowTick}
                  x2={endTick}
                  fill="rgba(120, 174, 232, 0.05)"
                  strokeOpacity={0}
                  label={{ value: t("energy.timeline.modal.zoneLabel"), position: "insideTopRight", fill: "#5f7080", fontSize: 11 }}
                />
                <XAxis dataKey="label" ticks={ticks} interval={0} tick={MODAL_TICK} axisLine={false} tickLine={false} />
                <YAxis
                  tick={MODAL_TICK}
                  tickCount={7}
                  axisLine={false}
                  tickLine={false}
                  width={Y_AXIS_WIDTH}
                  label={unitLabel("W")}
                />
                <Tooltip
                  content={<ModalTooltip series={visible} rawFc={rawFc} />}
                  cursor={{ stroke: "rgba(147, 165, 182, 0.45)" }}
                />
                <ReferenceLine
                  x={nowTick}
                  stroke="rgba(230, 237, 244, 0.35)"
                  strokeDasharray="3 3"
                  label={{ value: nowLabel, position: "top", fill: "#e6edf4", fontSize: 11, fontWeight: 600 }}
                />
                {/* 실선을 먼저, 점선을 나중에 그려 점선이 위에 오게 한다. */}
                {visible.map((item) => (
                  <Line
                    key={item.histKey}
                    type="monotone"
                    dataKey={item.histKey}
                    name={t(item.measuredKey)}
                    stroke={item.color}
                    strokeWidth={2.25}
                    dot={{ r: 3, strokeWidth: 0, fill: item.color }}
                    activeDot={{ r: 5, strokeWidth: 2, stroke: MODAL_BG, fill: item.color }}
                    isAnimationActive={false}
                  />
                ))}
                {visible.map((item) => (
                  <Line
                    key={item.fcKey}
                    type="monotone"
                    dataKey={item.fcKey}
                    name={t(item.forecastKey)}
                    stroke={item.color}
                    strokeWidth={2.25}
                    strokeDasharray="5 4"
                    dot={{ r: 3, strokeWidth: 0, fill: item.color }}
                    activeDot={{ r: 4.5, strokeWidth: 2, stroke: item.color, fill: MODAL_BG }}
                    isAnimationActive={false}
                  />
                ))}
                {placeholderSegment && (
                  <ReferenceLine segment={placeholderSegment} stroke="#FFA500" strokeWidth={2} strokeDasharray="5 4" />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="ei-split-legend ei-timeline-legend">
            <span className="ei-legend-key">
              <i className="solid" style={{ borderColor: "#93a5b6" }} />
              {t("energy.timeline.modal.measured")}
            </span>
            <span className="ei-legend-key">
              <i className="dash" style={{ borderColor: "#93a5b6" }} />
              {t("energy.timeline.modal.forecast")}
            </span>
            <span className="ei-legend-key">
              <i className="band" />
              {t("energy.timeline.modal.zone")}
            </span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
